import {
  PAGE_TREE_DELETED_ID,
  pagePartBlocksMap,
  pagePartElementsMap,
  type PageStructureEntry,
  type PageTree,
} from '@streamlain/database-identity';
import * as Y from 'yjs';

import type {
  AgentOrigin,
  AgentPointer,
  AgentPointerTarget,
} from './agent-pointers';

/** What one change of an agent step did to one object of the document. */
export type AgentPlaybackKind = 'create' | 'edit' | 'move' | 'delete';

export interface AgentPlaybackStep {
  readonly target: AgentPointerTarget;
  readonly kind: AgentPlaybackKind;
  /** The previous move bounds, or the retained bounds of a deleted object. */
  readonly before?: string;
  /** The span of an element text the edit inserted, as Y.Text indices after it. */
  readonly textInserted?: { readonly from: number; readonly to: number };
}

/**
 * The changes of one agent update in the order a person would make them. Every
 * client derives it from the update and its origin alone, so all of them play
 * back the same.
 */
export interface AgentPlayback {
  readonly runId: string;
  readonly stepId: string;
  readonly ownerUserId: string;
  readonly steps: readonly AgentPlaybackStep[];
}

/** From the first to the end of the last inserted text of a delta; none without an insert. */
function insertedSpan(
  delta: readonly { insert?: unknown; retain?: number }[]
): AgentPlaybackStep['textInserted'] {
  let index = 0;
  let from: number | undefined;
  let to = 0;
  for (const op of delta) {
    if (op.retain) index += op.retain;
    if (typeof op.insert !== 'string') continue;
    from ??= index;
    index += op.insert.length;
    to = index;
  }
  return from === undefined ? undefined : { from, to };
}

/** The keys one transaction changed in a shared type; its map is keyed by the changed types. */
function changedKeys(
  transaction: Y.Transaction,
  type: unknown
): Set<string | null> | undefined {
  const changed: ReadonlyMap<unknown, Set<string | null>> = transaction.changed;
  return changed.get(type);
}

/** The page as a playback reads it after the transaction. */
export interface AgentPlaybackPage {
  entry(id: string): PageStructureEntry | undefined;
  tree(): PageTree;
}

/** Fields whose change alone only moves or resizes an object. */
const PLACE_FIELDS = new Set(['xywh', 'prop:xywh']);

function kindOf(
  transaction: Y.Transaction,
  page: AgentPlaybackPage,
  target: AgentPointerTarget
): AgentPlaybackKind {
  // A row lives in a row shard of its database, not among the page's blocks.
  if (target.rowId) return 'edit';
  const holder =
    target.kind === 'block'
      ? pagePartBlocksMap(transaction.doc)
      : pagePartElementsMap(transaction.doc);
  // The fields come first: the entry of a new object may arrive after them.
  if (changedKeys(transaction, holder)?.has(target.id))
    return holder.has(target.id) ? 'create' : 'delete';
  const entry = page.entry(target.id);
  if (!entry || entry.p === PAGE_TREE_DELETED_ID) return 'delete';
  const fields = holder.get(target.id);
  const changed = fields instanceof Y.Map && changedKeys(transaction, fields);
  return changed &&
    changed.size > 0 &&
    [...changed].every(key => key !== null && PLACE_FIELDS.has(key))
    ? 'move'
    : 'edit';
}

/** Presentation data read from the same transaction, without another model write. */
function stepDetails(
  transaction: Y.Transaction,
  target: AgentPointerTarget,
  kind: AgentPlaybackKind
): Pick<AgentPlaybackStep, 'before' | 'textInserted'> {
  if (target.rowId) return {};
  const holder =
    target.kind === 'block'
      ? pagePartBlocksMap(transaction.doc)
      : pagePartElementsMap(transaction.doc);
  const fields = holder.get(target.id);
  if (!(fields instanceof Y.Map)) return {};
  let before: string | undefined;
  if (kind === 'move') {
    const changed = changedKeys(transaction, fields);
    if (changed) {
      const keys = new Y.YMapEvent(fields, transaction, changed).changes.keys;
      for (const key of PLACE_FIELDS) {
        const previous = keys.get(key)?.oldValue;
        if (typeof previous === 'string') {
          before = previous;
          break;
        }
      }
    }
  } else if (kind === 'delete') {
    for (const key of PLACE_FIELDS) {
      const retained = fields.get(key);
      if (typeof retained === 'string') {
        before = retained;
        break;
      }
    }
  }
  let textInserted: AgentPlaybackStep['textInserted'];
  if (kind === 'edit' && target.kind === 'canvas_element') {
    const text = fields.get('text');
    const changed = text instanceof Y.Text && changedKeys(transaction, text);
    if (changed)
      textInserted = insertedSpan(
        new Y.YTextEvent(text, transaction, changed).delta
      );
  }
  return {
    ...(before === undefined ? {} : { before }),
    ...(textInserted === undefined ? {} : { textInserted }),
  };
}

/** Position of each block in reading order: depth first through the page tree. */
function readingOrder(tree: PageTree): Map<string, number> {
  const order = new Map<string, number>();
  const visit = (id: string) => {
    order.set(id, order.size);
    for (const child of tree.children.get(id) ?? []) visit(child);
  };
  if (tree.root) visit(tree.root);
  return order;
}

/**
 * The playback of an agent update in one part document of the page `pointer`
 * stands on. Blocks come in reading order, canvas objects in the order the step
 * named them; blocks keep the places blocks had among the targets.
 */
export function agentPlaybackOf(
  transaction: Y.Transaction,
  page: AgentPlaybackPage,
  origin: AgentOrigin,
  pointer: AgentPointer
): AgentPlayback {
  const blocks = pointer.targets.filter(target => target.kind === 'block');
  if (blocks.length > 1) {
    const order = readingOrder(page.tree());
    const at = (target: AgentPointerTarget) =>
      order.get(target.id) ?? Number.MAX_SAFE_INTEGER;
    blocks.sort((a, b) => at(a) - at(b));
  }
  let block = 0;
  return {
    runId: origin.runId,
    stepId: origin.stepId,
    ownerUserId: origin.ownerUserId,
    steps: pointer.targets.map(target => {
      const ordered = target.kind === 'block' ? blocks[block++] : target;
      const kind = kindOf(transaction, page, ordered);
      return {
        target: ordered,
        kind,
        ...stepDetails(transaction, ordered, kind),
      };
    }),
  };
}
